import PouchDB from '@/repos/PouchDB';

// Delete a doc by id, tolerating a concurrent sync that removes or updates it
// between the read and the remove. Resolves the removed doc, or null when the
// doc is already gone -- deleting something that's already gone is the caller's
// desired end state, not an error (HAMRS-1E).
//
// PouchDB.get 404s when the doc is missing. PouchDB.remove can still 409 if a
// sync lands between the get and the remove: the doc's winning revision changed
// under us. We don't map that 409 straight to "deleted" -- it might be a
// concurrent *update*, and silently skipping the delete would leave the doc
// behind. Instead we re-read the winning revision on one retry: if it's now a
// deletion the get 404s (-> null); if it's an update we remove that revision.
export async function deleteDocIfPresent<T = unknown>(id: string): Promise<T | null> {
  // Two passes: the first can lose a race to a concurrent update; the retry
  // acts on the winning revision. A second conflict in a row is real contention
  // and is surfaced to the caller rather than looping.
  for (let attempt = 0; attempt < 2; attempt++) {
    let doc;
    try {
      doc = await PouchDB.get(id);
    } catch (error) {
      if ((error as { status?: number })?.status === 404) return null;
      throw error;
    }
    try {
      await PouchDB.remove(doc);
      return doc as unknown as T;
    } catch (error) {
      if ((error as { status?: number })?.status === 409 && attempt === 0) continue;
      throw error;
    }
  }
  // Unreachable: every path in the loop returns or throws.
  return null;
}
